import React, { useContext } from "react";
import { useTranslation } from "utilities/i18n";
import {
  DeviceFilter,
  ActionFilter,
  areFiltersSelected,
  actionFilterSelected,
  useDeviceFilterValue,
  DeviceFilterState
} from "elements";
import { FilterPanel } from "collections/FilterPanel";
import { DeviceAction, InsightList } from "collections/InsightDetails";
import {
  getFieldAndCountWithEntries,
  getSitesFromActions
} from "utilities/filters/deviceFilters";
import {
  useActionsFilter,
  ActionFilterState
} from "routes/Manage/Actions/QueryParam";
import useDeviceAction, { UseDeviceAction } from "./useDeviceAction";
import { stringify } from "polyrouter";
import { Layout } from "views/Main/Layouts";
import { DEVICE_ACTION_TYPES } from "Constants";
import queryString from "query-string";
import { useHistory } from "react-router";
import ConfigureNotifications from "./ConfigureNotifications";
import { usePermissions } from "rbac";
import { SelectedTenantContext } from "polyrouter/SelectedTenantProvider";

export interface DeviceActionsProps {
  actions: Array<UseDeviceAction>;
  loading: boolean;
}

const { Content } = Layout;

export const DeviceActionsLoader: React.FC = () => {
  const { data, fetching, error } = useDeviceAction();
  if (error) {
    return <span className="graphql-error">{error.message}</span>;
  }
  return <DeviceActionContent actions={data} loading={fetching} />;
};

type TopicDescriptors =
  | "TOPIC_AVAILABLE_UPDATES_DESCRIPTION"
  | "TOPIC_AVAILABLE_UPDATES_DESCRIPTION_2"
  | "TOPIC_OFFLINE_DEVICES_DESCRIPTION"
  | "TOPIC_SITE_UNASSIGNED_DESCRIPTION";

type TopicTitles =
  | "TOPIC_AVAILABLE_UPDATES_SHORT_TITLE"
  | "TOPIC_AVAILABLE_UPDATES_TITLE"
  | "TOPIC_OFFLINE_DEVICES_SHORT_TITLE"
  | "TOPIC_OFFLINE_DEVICES_TITLE"
  | "TOPIC_OFFLINE_DEVICES_TITLE_other"
  | "TOPIC_SITE_UNASSIGNED_SHORT_TITLE"
  | "TOPIC_SITE_UNASSIGNED_TITLE"
  | "TOPIC_SITE_UNASSIGNED_TITLE_other";

export const DeviceActionContent: React.FC<DeviceActionsProps> = ({
  actions = [],
  loading
}) => {
  const { t } = useTranslation();
  const [filterState] = useDeviceFilterValue();

  const resolveActionDetails = {
    AVAILABLE_UPDATES: {
      link: (a: UseDeviceAction) => {
        return {
          pathname: "/manage/inventory/table",
          search: stringify({
            DeviceFilterState: {
              site: filterState.site,
              model: a.model,
              isUpdateAvailable: "true",
              updateVersion: a.version
            }
          })
        };
      },
      modelDefault: t("UNKNOWN_MODEL"),
      description: (a: UseDeviceAction) => {
        const topicDescriptionKey: TopicDescriptors = `TOPIC_${a.action}_DESCRIPTION` as const;
        let secondaryDescription = "";

        if ("AVAILABLE_UPDATES" === a.action) {
          const topicDescription2Key: TopicDescriptors = `TOPIC_${a.action}_DESCRIPTION_2` as const;
          secondaryDescription = t(topicDescription2Key, {
            count: a.locations.length,
            location: a.locations[0] || ""
          });
        }

        return (
          t(topicDescriptionKey, {
            version: a.version,
            count: a.count,
            model: a.model
          }) + secondaryDescription
        );
      }
    },
    OFFLINE_DEVICES: {
      link: (a: UseDeviceAction) => {
        return {
          pathname: "/manage/inventory/table",
          search: stringify({
            DeviceFilterState: {
              site: filterState.site,
              connected: "false",
              model: a.model
            }
          })
        };
      },
      modelDefault: t("UNKNOWN_MODEL"),
      description: (a: UseDeviceAction) => {
        const topicDescriptionKey: TopicDescriptors = `TOPIC_${a.action}_DESCRIPTION` as const;
        return t(topicDescriptionKey, {
          count: a.count,
          model: a.model
        });
      }
    },
    SITE_UNASSIGNED: {
      link: () => ({ pathname: "/manage/sites/" }),
      modelDefault: t("DEVICE_PLAIN_other"),
      description: (a: UseDeviceAction) => {
        const topicDescriptionKey: TopicDescriptors = `TOPIC_${a.action}_DESCRIPTION` as const;
        return t(topicDescriptionKey);
      }
    }
  };

  const devices: Array<DeviceAction> = actions.map((a: UseDeviceAction) => {
    const topicTitleKey: TopicTitles = `TOPIC_${a.action}_TITLE` as const;

    return {
      model: a.model,
      locations: Array.from(a.locations.values()).filter(
        (value: any): value is string => typeof value === "string"
      ),
      title: t(topicTitleKey, {
        model: a.model || resolveActionDetails[a.action].modelDefault,
        count: a.count
      }),
      count: a.count,
      action: a.action,
      details: resolveActionDetails[a.action].description(a),
      link: resolveActionDetails[a.action].link(a),
      linkTitle: t("VIEW_DEVICES"),
      version: a.version
    };
  });

  return (
    <Content scrolling data-testid="expandable-card-test">
      <Content.ScrollingList loading={loading}>
        <InsightList actionsList={devices} />
      </Content.ScrollingList>
    </Content>
  );
};

const DeviceActionsSideBar: React.FC = () => {
  const { t } = useTranslation();
  const [filterState, setFilter] = useDeviceFilterValue();
  const [actionFilterState, setActionFilter] = useActionsFilter();
  const { data, fetching } = useDeviceAction();
  const { tenant } = useContext(SelectedTenantContext);
  const tenantId = tenant?.id ?? "";
  const hasPermission = usePermissions();
  const hasPermissionToNotificationCenter = hasPermission(
    "nc:core:readActions",
    tenantId
  );
  const getFieldAndCount = getFieldAndCountWithEntries<UseDeviceAction>(data);
  const modelSelector = (a: UseDeviceAction) => a.model;
  const models = getFieldAndCount(modelSelector);
  const sites = getSitesFromActions(data);
  const deviceActions = [];
  for (const [key, value] of Object.entries(DEVICE_ACTION_TYPES)) {
    if (data.some(item => item.action === key)) {
      deviceActions.push({
        text: value,
        value: value
      });
    }
  }

  type AllFilterValues = DeviceFilterState & ActionFilterState;
  const anyFilterSelected = (filterValues: AllFilterValues): boolean => {
    const { action, ...rest } = filterValues;
    return actionFilterSelected({ action: action }) || areFiltersSelected(rest);
  };

  const history = useHistory();
  const resetAllFilters = (names: Array<string>) => {
    let hash = history.location.hash;
    let query = queryString.parse(history.location.search);
    for (let name of names) {
      delete query[name];
    }
    history.replace({
      search: stringify(query),
      hash: hash
    });
  };

  return (
    <Layout.Sidebar>
      <FilterPanel
        filters={[
          <DeviceFilter
            title={t("DEVICE_MODEL")}
            field="model"
            options={models.map(({ value }) => ({
              text: value,
              value: value
            }))}
            filterValues={filterState}
            onChange={value =>
              setFilter({
                ...filterState,
                model: value
              })
            }
            loading={fetching}
            search
            clearable
          />,
          <DeviceFilter
            title={t("SITE")}
            field="site"
            options={sites.map(({ value }) => ({
              text: value,
              value: value
            }))}
            enableTooltip
            filterValues={filterState}
            onChange={value =>
              setFilter({
                ...filterState,
                site: value
              })
            }
            loading={fetching}
            search
            clearable
          />,
          <ActionFilter
            title={t("ACTION")}
            field="action"
            options={deviceActions}
            enableTooltip
            filterValues={actionFilterState}
            onChange={value =>
              setActionFilter({
                action: value !== "" ? value : undefined
              })
            }
            loading={fetching}
            search
            clearable
          />
        ]}
        metric={{
          type: t("ACTIONS"),
          count: data.length
        }}
        iconName="devices"
        subHeader={t("DEVICE_ACTIONS_SUB_HEADER")}
        areFiltersSelected={anyFilterSelected}
        filterValues={{ ...filterState, ...actionFilterState }}
        resetFilters={() =>
          resetAllFilters(["ActionsFilterState", "DeviceFilterState"])
        }
      />
      {hasPermissionToNotificationCenter && (
        <ConfigureNotifications tenantId={tenantId} />
      )}
    </Layout.Sidebar>
  );
};

export const DeviceActions: React.FC = () => {
  return (
    <Layout>
      <DeviceActionsSideBar />
      <DeviceActionsLoader />
    </Layout>
  );
};

export default DeviceActions;
